// src/components/Town/utils/fetchExpenditureSections.js

import { getDoc, doc } from "firebase/firestore";
import { db } from "../../../firebase";

/**
 * New Firestore shape:
 *   townGovernment/<docId>
 *
 * Examples:
 * - expenditures_town:
 *     "2020": { "Approved": { "<Section>": { "<Item>": n } ... }, "Actual": {...} }
 *
 * - expenditures_education:
 *     "2020": { "Approved": { "Items": { "Education": n }, "Total": n }, "Actual": {...} }
 *
 * - expenditures_debt_transfers_out:
 *     "2020": {
 *        "Approved": { "Debt Transfers Out": {...}, "Debt Service": {...} },
 *        "Actual":   { ... }
 *     }
 */

const DOC_ID_BY_GROUP = {
  town: "expenditures_town",
  debtServiceAndTransfers: "expenditures_debt_transfers_out",
  education: "expenditures_education",
};

function formatLabel(key) {
  return String(key)
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^./, (str) => str.toUpperCase());
}

function sectionCandidatesForType(type) {
  // Your columnBuilder types are: adopted | actual
  // Your new docs use: Approved | Actual
  // Some older configs / docs may still use Adopted.
  if (type === "adopted") return ["Approved", "Adopted"];
  if (type === "actual") return ["Actual"];
  return [];
}

function resolveYearTypeNode(docData, year, type) {
  const yearNode = docData?.[String(year)];
  if (!yearNode) return null;

  for (const k of sectionCandidatesForType(type)) {
    if (yearNode?.[k] && typeof yearNode[k] === "object") return yearNode[k];
  }
  return null;
}

function normalizeSectionName(groupKey, sectionName) {
  // Keep naming aligned with your UI + historical expectations
  if (groupKey === "debtServiceAndTransfers") {
    if (sectionName === "Debt Transfers Out") return "Transfers Out";
    // keep "Debt Service" as-is
  }
  return sectionName;
}

/**
 * Extract entries for a single (year,type) from the new per-doc format.
 * Returns: [{ sectionName, label, value }, ...]
 */
function extractEntries(docData, year, groupKey, type) {
  const typeNode = resolveYearTypeNode(docData, year, type);
  if (!typeNode) return [];

  // Remove "Total" from consideration at any level (case-insensitive)
  const entriesNoTotal = (obj) =>
    Object.entries(obj || {}).filter(([k]) => !/^total$/i.test(String(k)));

  const results = [];

  // Variant A: { Items: { ... }, Total: n }  (e.g., education, many single-section docs)
  if (typeNode.Items && typeof typeNode.Items === "object") {
    const sectionName =
      docData?.fullName ||
      docData?.name ||
      formatLabel(groupKey || "Section");

    for (const [label, value] of entriesNoTotal(typeNode.Items)) {
      results.push({
        sectionName: normalizeSectionName(groupKey, sectionName),
        label,
        value: value ?? null,
      });
    }
    return results;
  }

  // Variant B: nested blocks (Town doc, Debt doc)
  // typeNode = { "<SectionName>": { "<Item>": n }, ... }
  const blockPairs = entriesNoTotal(typeNode);

  const hasNestedBlocks = blockPairs.some(
    ([, v]) => v && typeof v === "object" && !Array.isArray(v)
  );

  if (hasNestedBlocks) {
    for (const [sectionNameRaw, itemsObj] of blockPairs) {
      const sectionName = normalizeSectionName(groupKey, sectionNameRaw);

      // itemsObj might itself contain a nested "Items" wrapper in some docs
      const items =
        itemsObj?.Items && typeof itemsObj.Items === "object"
          ? itemsObj.Items
          : itemsObj;

      for (const [label, value] of entriesNoTotal(items)) {
        results.push({
          sectionName,
          label,
          value: value ?? null,
        });
      }
    }
    return results;
  }

  // Variant C (fallback): flat items directly under Approved/Actual
  const wrapperName =
    docData?.fullName ||
    docData?.name ||
    formatLabel(groupKey || "Section");

  for (const [label, value] of blockPairs) {
    results.push({
      sectionName: normalizeSectionName(groupKey, wrapperName),
      label,
      value: value ?? null,
    });
  }

  return results;
}

export async function fetchExpenditureSections(groupKey, baseColumns, docOverride = null) {
  const docId = DOC_ID_BY_GROUP[groupKey];

  if (!docId) {
    console.warn("fetchExpenditureSections: unknown groupKey:", groupKey);
    return { sections: [], rawDoc: {} };
  }

  let docData = docOverride;

  if (!docData) {
    const snap = await getDoc(doc(db, "townGovernment", docId));
    if (!snap.exists()) {
      console.warn(`fetchExpenditureSections: missing doc townGovernment/${docId}`);
      return { sections: [], rawDoc: {} };
    }

    docData = snap.data();
  }

  const dataCols = (baseColumns || []).slice(1);

  // sectionsMap[sectionName][label] => { label, y0_adopted: n, ... }
  const sectionsMap = {};

  for (const col of dataCols) {
    const { year, type, key } = col;
    if (!year || !type || !key) continue;

    const entries = extractEntries(docData, year, groupKey, type);

    for (const { sectionName, label, value } of entries) {
      if (!sectionsMap[sectionName]) sectionsMap[sectionName] = {};
      if (!sectionsMap[sectionName][label]) sectionsMap[sectionName][label] = { label };

      sectionsMap[sectionName][label][key] =
        typeof value === "number" && Number.isFinite(value) ? value : value ?? null;
    }
  }

  const sections = Object.entries(sectionsMap).map(([name, rowsObj]) => ({
    name,
    rows: Object.values(rowsObj),
  }));

  return { sections, rawDoc: docData };
}
